import React, { useEffect, useRef, useState } from 'react';

const VideoPlayer = ({ url }) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);

  // Función de play/pause
  const togglePlay = () => {
    if (videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current.play();
        setIsPlaying(true);
      } else {
        videoRef.current.pause();
        setIsPlaying(false);
      }
    }
  };

  // Función de actualización de progreso
  const handleProgress = () => {
    if (videoRef.current) {
      const currentTime = videoRef.current.currentTime;
      const videoDuration = videoRef.current.duration;
      setProgress((currentTime / videoDuration) * 100);
      setDuration(videoDuration);
    }
  };

  // Función para actualizar el tiempo del video
  const seek = (e) => {
    const seekTime = (e.nativeEvent.offsetX / e.target.offsetWidth) * duration;
    videoRef.current.currentTime = seekTime;
  };

  // Función para cambiar el volumen
  const changeVolume = (e) => {
    const newVolume = e.target.value;
    setVolume(newVolume);
    videoRef.current.volume = newVolume;
  };

  // Función de mute
  const toggleMute = () => {
    setIsMuted((prev) => !prev);
    videoRef.current.muted = !isMuted;
  };

  // Prevenir descarga y acciones no deseadas
  useEffect(() => {
    const video = videoRef.current;
    const container = containerRef.current;

    if (video) {
      video.addEventListener('timeupdate', handleProgress);

      // Prevenir clic derecho (menú contextual)
      const preventContextMenu = (e) => {
        e.preventDefault();
        return false;
      };

      // Prevenir drag and drop
      const preventDrag = (e) => {
        e.preventDefault();
        return false;
      };

      // Prevenir atajos de teclado relacionados con descarga
      const preventKeyboardShortcuts = (e) => {
        // Prevenir Ctrl+S, Ctrl+Shift+I, F12, etc.
        if ((e.ctrlKey || e.metaKey) && (e.key === 's' || e.key === 'S')) {
          e.preventDefault();
          return false;
        }
        if ((e.ctrlKey || e.metaKey) && (e.shiftKey && (e.key === 'I' || e.key === 'i'))) {
          e.preventDefault();
          return false;
        }
        if (e.key === 'F12') {
          e.preventDefault();
          return false;
        }
      };

      // Prevenir selección de texto
      const preventSelection = (e) => {
        if (e.target === video || e.target === container) {
          e.preventDefault();
          return false;
        }
      };

      video.addEventListener('contextmenu', preventContextMenu);
      video.addEventListener('dragstart', preventDrag);
      video.addEventListener('selectstart', preventSelection);
      container?.addEventListener('contextmenu', preventContextMenu);
      container?.addEventListener('dragstart', preventDrag);
      container?.addEventListener('selectstart', preventSelection);
      document.addEventListener('keydown', preventKeyboardShortcuts);

      return () => {
        video.removeEventListener('timeupdate', handleProgress);
        video.removeEventListener('contextmenu', preventContextMenu);
        video.removeEventListener('dragstart', preventDrag);
        video.removeEventListener('selectstart', preventSelection);
        container?.removeEventListener('contextmenu', preventContextMenu);
        container?.removeEventListener('dragstart', preventDrag);
        container?.removeEventListener('selectstart', preventSelection);
        document.removeEventListener('keydown', preventKeyboardShortcuts);
      };
    }
  }, []);

  return (
    <div 
      ref={containerRef}
      className="video-container" 
      style={styles.container}
      onContextMenu={(e) => e.preventDefault()}
      onDragStart={(e) => e.preventDefault()}
    >
      <video
        ref={videoRef}
        src={url}
        style={styles.video}
        onClick={togglePlay}
        controls={false}
        controlsList="nodownload nofullscreen noremoteplayback"
        disablePictureInPicture
        preload="metadata"
        onContextMenu={(e) => e.preventDefault()}
        onDragStart={(e) => e.preventDefault()}
      />
      <div style={styles.controls}>
        <button onClick={togglePlay} style={styles.playPauseButton}>
          {isPlaying ? '❚❚' : '►'}
        </button>
        <div style={styles.progressBarContainer} onClick={seek}>
          <div
            style={{
              ...styles.progressBar,
              width: `${progress}%`,
            }}
          />
        </div>
        <div style={styles.timeDisplay}>
          <span>{formatTime(videoRef.current?.currentTime || 0)}</span> /{' '}
          <span>{formatTime(duration)}</span>
        </div>
        <div style={styles.volumeContainer}>
          <button onClick={toggleMute} style={styles.volumeButton}>
            {isMuted ? '🔇' : '🔊'}
          </button>
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={volume}
            onChange={changeVolume}
            style={styles.volumeSlider}
          />
        </div>
      </div>
    </div>
  );
};

// Función para formatear el tiempo
const formatTime = (seconds) => {
  const date = new Date(seconds * 1000);
  return date.toISOString().substr(14, 5); // Formato mm:ss
};

// Estilos mejorados
const styles = {
  container: {
    position: 'relative',
    width: '100%',
    maxWidth: '800px', // Limitar el tamaño máximo del reproductor
    borderRadius: '12px',
    boxShadow: '0 2px 15px rgba(0, 0, 0, 0.5)',
    backgroundColor: '#000',
    overflow: 'hidden',
    userSelect: 'none',
    WebkitUserSelect: 'none',
    MozUserSelect: 'none',
    msUserSelect: 'none',
  },
  video: {
    width: '100%',
    borderRadius: '12px',
    userSelect: 'none',
    WebkitUserSelect: 'none',
    MozUserSelect: 'none',
    msUserSelect: 'none',
    pointerEvents: 'auto',
  },
  controls: {
    position: 'absolute',
    bottom: '0',
    left: '0',
    right: '0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 20px',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    borderRadius: '0 0 12px 12px',
  },
  playPauseButton: {
    background: 'rgba(255, 255, 255, 0.7)',
    color: '#333',
    border: 'none',
    fontSize: '20px',
    padding: '10px 15px',
    borderRadius: '50%',
    cursor: 'pointer',
    transition: 'background 0.2s ease',
  },
  progressBarContainer: {
    flexGrow: 1,
    height: '6px',
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
    borderRadius: '3px',
    margin: '0 20px',
    cursor: 'pointer',
  },
  progressBar: {
    height: '100%',
    backgroundColor: '#ff0000',
    borderRadius: '3px',
  },
  timeDisplay: {
    color: 'white',
    fontSize: '14px',
  },
  volumeContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  volumeButton: {
    background: 'none',
    border: 'none',
    color: 'white',
    fontSize: '20px',
    cursor: 'pointer',
    marginRight: '10px',
  },
  volumeSlider: {
    width: '80px',
    accentColor: '#ff0000',
    borderRadius: '5px',
  },
};

export default VideoPlayer;
